import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, Calculator, MessageSquare, Phone, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

interface EnhancedCTAProps {
  variant?: 'sticky' | 'popup' | 'inline';
  service?: string;
  triggerScroll?: number;
  className?: string;
}

const EnhancedCTA: React.FC<EnhancedCTAProps> = ({ 
  variant = 'sticky', 
  service,
  triggerScroll = 500,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();

  const translations = {
    de: {
      title: 'Kostenloses Erstgespräch vereinbaren',
      subtitle: 'Lassen Sie uns über Ihr Projekt sprechen',
      calculator: 'Kosten berechnen',
      contact: 'Kontakt aufnehmen',
      call: 'Anrufen',
      close: 'Schließen',
      minimize: 'Minimieren'
    },
    en: {
      title: 'Schedule Free Consultation',
      subtitle: 'Let\'s discuss your project',
      calculator: 'Calculate Costs',
      contact: 'Get in Touch',
      call: 'Call Now',
      close: 'Close',
      minimize: 'Minimize'
    },
    fr: {
      title: 'Planifier une consultation gratuite',
      subtitle: 'Discutons de votre projet',
      calculator: 'Calculer les coûts',
      contact: 'Prendre contact',
      call: 'Appeler',
      close: 'Fermer',
      minimize: 'Réduire'
    },
    it: {
      title: 'Prenota consulenza gratuita',
      subtitle: 'Parliamo del tuo progetto',
      calculator: 'Calcola i costi',
      contact: 'Contattaci',
      call: 'Chiama',
      close: 'Chiudi',
      minimize: 'Riduci'
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  useEffect(() => {
    if (variant !== 'inline') {
      const handleScroll = () => {
        const shouldShow = window.scrollY > triggerScroll;
        setIsVisible(shouldShow);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setIsVisible(true);
    }
  }, [variant, triggerScroll]);

  useEffect(() => {
    if (variant === 'popup' && isVisible) {
      // Auto-show popup after delay
      const timer = setTimeout(() => {
        if (!localStorage.getItem('cta-popup-dismissed')) {
          setIsVisible(true);
        }
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [variant, isVisible]);

  const handleCalculatorClick = () => {
    const calculatorPath = location.pathname.includes('seo') 
      ? `/${currentLanguage}/seo-roi-rechner`
      : `/${currentLanguage}/website-kosten-rechner`;
    navigate(calculatorPath);
  };

  const handleContactClick = () => {
    const contactPath = `/${currentLanguage}/${
      currentLanguage === 'de' ? 'kontakt' : 
      currentLanguage === 'it' ? 'contatto' : 'contact'
    }`;
    navigate(contactPath);
  };

  const handleCallClick = () => {
    window.open('tel:+41449992020', '_self');
  };

  const handleClose = () => {
    setIsVisible(false);
    if (variant === 'popup') {
      localStorage.setItem('cta-popup-dismissed', 'true');
    }
  };

  if (!isVisible) return null;

  const renderContent = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="font-bold text-lg text-foreground mb-2">
          {t.title}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t.subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Button
          onClick={handleCalculatorClick}
          variant="outline"
          size="sm"
          className="flex items-center justify-center"
        >
          <Calculator className="w-4 h-4 mr-2" />
          {t.calculator}
        </Button>

        <Button
          onClick={handleContactClick}
          size="sm"
          className="neo-button flex items-center justify-center"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          {t.contact}
        </Button>

        <Button
          onClick={handleCallClick}
          variant="secondary"
          size="sm"
          className="flex items-center justify-center"
        >
          <Phone className="w-4 h-4 mr-2" />
          {t.call}
        </Button>
      </div>
    </div>
  );

  const renderMinimized = () => (
    <div className="flex items-center space-x-2">
      <Button
        onClick={handleCalculatorClick}
        size="sm"
        className="neo-button"
      >
        <Calculator className="w-4 h-4" />
      </Button>
      <Button
        onClick={() => setIsMinimized(false)}
        variant="outline"
        size="sm"
      >
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );

  if (variant === 'inline') {
    return (
      <Card className={`neo-card ${className}`}>
        <CardContent className="p-6">
          {renderContent()}
        </CardContent>
      </Card>
    );
  }

  if (variant === 'popup') {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-xl p-6 max-w-md w-full relative"
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="absolute top-2 right-2"
              >
                <X className="w-4 h-4" />
              </Button>
              {renderContent()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Sticky variant
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 max-w-md w-full mx-4"
    >
      <Card className="neo-card bg-card/95 backdrop-blur-md border border-border/50">
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              {isMinimized ? renderMinimized() : renderContent()}
            </div>
            <div className="flex space-x-1 ml-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                <ArrowRight className={`w-4 h-4 transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default EnhancedCTA;